import React, { useState, useMemo } from 'react';
import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Search, Trash2, Recycle, AlertTriangle } from 'lucide-react';
import { wasteItems } from '@/data/wasteData';

const WasteSortingABCPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLetter, setSelectedLetter] = useState('A');

  const alphabet = 'ABCDEFGHKLMNPRSŠTVŽ'.split('');

  const filteredItems = useMemo(() => {
    if (!searchTerm) return wasteItems;
    return wasteItems.filter(item =>
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm]);

  const itemsByLetter = useMemo(() => {
    return filteredItems.filter(item => 
      item.name.toUpperCase().startsWith(selectedLetter)
    );
  }, [filteredItems, selectedLetter]);

  const getContainerColor = (type) => {
    const colors = {
      'Stiklas': 'bg-recycle-glass',
      'Popierius': 'bg-recycle-paper',
      'Plastikas': 'bg-recycle-plastic',
      'Mišrios atliekos': 'bg-recycle-mixed',
      'Pavojingos': 'bg-destructive',
    };
    return colors[type] || 'bg-muted';
  };

  const getContainerIcon = (type) => {
    if (type === 'Pavojingos') return AlertTriangle;
    if (type === 'Mišrios atliekos') return Trash2;
    return Recycle;
  };

  return (
    <div className="min-h-screen">
      <Navigation />

      {/* Header */}
      <section className="pt-24 pb-12 bg-gradient-subtle">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <Badge className="bg-primary/10 text-primary hover:bg-primary/20">
              Rūšiavimo Vadovas
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold">
              Rūšiavimo ABC
            </h1>
            <p className="text-lg text-muted-foreground">
              Suraskite bet kurį daiktą ir sužinokite, kaip jį teisingai išmesti
            </p>
          </div>
        </div>
      </section>

      {/* Search Section */}
      <section className="py-8 border-b">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Ieškokite atliekų pavadinimo..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-12 h-14 text-lg"
            />
          </div>
        </div>
      </section>

      {/* Alphabet Navigation */}
      <section className="py-6 bg-muted/50 sticky top-16 z-40 border-b">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap gap-2 justify-center">
            {alphabet.map((letter) => (
              <button
                key={letter}
                onClick={() => setSelectedLetter(letter)}
                className={`w-10 h-10 rounded-lg font-semibold transition-smooth ${
                  selectedLetter === letter
                    ? 'bg-primary text-white'
                    : 'bg-background hover:bg-muted text-foreground'
                }`}
              >
                {letter}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Items List */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            {itemsByLetter.length === 0 ? (
              <div className="text-center py-16">
                <Trash2 className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold mb-2">Nieko nerasta</h3>
                <p className="text-muted-foreground">
                  Pabandykite kitu paieškos žodžiu arba pasirinkite kitą raidę
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {itemsByLetter.map((item, index) => {
                  const Icon = getContainerIcon(item.container);
                  return (
                    <Card key={index} className="hover-lift transition-smooth">
                      <CardHeader>
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <CardTitle className="text-xl mb-2">{item.name}</CardTitle>
                            <div className="flex flex-wrap gap-2 mb-3">
                              <Badge className={`${getContainerColor(item.container)} text-white`}>
                                <Icon className="w-3 h-3 mr-1" />
                                {item.container}
                              </Badge>
                            </div>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <p className="text-muted-foreground leading-relaxed whitespace-pre-line">
                          {item.description}
                        </p>
                        {item.additionalInfo && (
                          <div className="mt-4 p-4 bg-muted rounded-lg">
                            <p className="text-sm text-muted-foreground">
                              <strong>Papildoma informacija:</strong> {item.additionalInfo}
                            </p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default WasteSortingABCPage;